import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { formatCurrency } from "../lib/formatCurrency";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Search, Package, Truck, FileText, CheckCircle2, Clock, AlertTriangle, MoreVertical } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  draft: 'bg-slate-100 text-slate-700',
  pending_approval: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  received: 'bg-purple-100 text-purple-700',
  invoiced: 'bg-indigo-100 text-indigo-700',
  matched: 'bg-emerald-100 text-emerald-700',
  paid: 'bg-emerald-100 text-emerald-700'
};


// PO Form Component
const PurchaseOrderForm = ({ po, projects, onSave, onClose }) => {
  const [form, setForm] = useState(po || {
    supplier_name: '', supplier_email: '', items: [{ description: '', quantity: 1, unit_price: 0 }],
    subtotal: 0, vat_rate: 15, vat_amount: 0, total: 0, currency: 'ZAR',
    delivery_date: '', notes: '', project_id: ''
  });
  const [saving, setSaving] = useState(false);

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const vatAmount = subtotal * (form.vat_rate / 100);
    return { subtotal, vat_amount: vatAmount, total: subtotal + vatAmount };
  };

  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], [field]: value };
    const totals = calculateTotals(newItems);
    setForm({ ...form, items: newItems, ...totals });
  };

  const addItem = () => {
    setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit_price: 0 }] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (po?.id) {
        await axios.put(`${API}/purchase-orders/${po.id}`, form);
        toast.success('Purchase order updated');
      } else {
        await axios.post(`${API}/purchase-orders`, form);
        toast.success('Purchase order created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to save purchase order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Supplier Name *</Label>
          <Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} required data-testid="po-supplier-input" />
        </div>
        <div className="space-y-2">
          <Label>Supplier Email</Label>
          <Input type="email" value={form.supplier_email} onChange={(e) => setForm({ ...form, supplier_email: e.target.value })} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Linked Project</Label>
          <Select value={form.project_id || ''} onValueChange={(v) => setForm({ ...form, project_id: String(v) })}>
            <SelectTrigger><SelectValue placeholder="Select project (optional)" /></SelectTrigger>
            <SelectContent>
              {projects.map(p => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Expected Delivery</Label>
          <Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Line Items</Label>
        <div className="border rounded-lg divide-y">
          {form.items.map((item, idx) => (
            <div key={idx} className="p-3 grid grid-cols-4 gap-2 items-end">
              <div className="col-span-2">
                <Input placeholder="Description" value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} />
              </div>
              <Input type="number" placeholder="Qty" min="1" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)} />
              <Input type="number" placeholder="Unit Price" min="0" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} />
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addItem}>+ Add Item</Button>
      </div>

      <div className="grid grid-cols-3 gap-4 text-right pt-4 border-t">
        <div></div>
        <div className="text-sm text-muted-foreground">Subtotal:</div>
        <div className="font-mono font-semibold">{formatCurrency(form.subtotal)}</div>
        <div></div>
        <div className="text-sm text-muted-foreground">VAT ({form.vat_rate}%):</div>
        <div className="font-mono">{formatCurrency(form.vat_amount)}</div>
        <div></div>
        <div className="font-semibold">Total:</div>
        <div className="font-mono font-bold text-lg">{formatCurrency(form.total)}</div>
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="save-po-btn">{saving ? 'Saving...' : 'Save PO'}</Button>
      </div>
    </form>
  );
};

// Goods Receipt Form
const GoodsReceiptForm = ({ po, onSave, onClose }) => {
  const [form, setForm] = useState({
    po_id: po.id,
    received_items: po.items.map((item, idx) => ({
      item_id: idx,
      description: item.description,
      ordered: item.quantity,
      quantity_received: item.quantity,
      notes: ''
    })),
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/purchase-orders/${po.id}/receive-goods`, form);
      toast.success('Goods receipt recorded');
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to record goods receipt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 bg-muted rounded-lg">
        <p className="font-semibold">{po.po_number}</p>
        <p className="text-sm text-muted-foreground">Supplier: {po.supplier_name}</p>
      </div>

      <div className="space-y-2">
        <Label>Items Received</Label>
        <div className="border rounded-lg divide-y">
          {form.received_items.map((item, idx) => (
            <div key={idx} className="p-3 grid grid-cols-4 gap-2 items-center">
              <div className="col-span-2">
                <p className="text-sm font-medium">{item.description}</p>
                <p className="text-xs text-muted-foreground">Ordered: {item.ordered}</p>
              </div>
              <Input
                type="number"
                min="0"
                max={item.ordered}
                value={item.quantity_received}
                onChange={(e) => {
                  const newItems = [...form.received_items];
                  newItems[idx].quantity_received = parseInt(e.target.value) || 0;
                  setForm({ ...form, received_items: newItems });
                }}
              />
              <Input
                placeholder="Notes"
                value={item.notes}
                onChange={(e) => {
                  const newItems = [...form.received_items];
                  newItems[idx].notes = e.target.value;
                  setForm({ ...form, received_items: newItems });
                }}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Receipt Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="confirm-receipt-btn">
          {saving ? 'Processing...' : 'Confirm Receipt'}
        </Button>
      </div>
    </form>
  );
};

// Supplier Invoice Form
const SupplierInvoiceForm = ({ po, onSave, onClose }) => {
  const [form, setForm] = useState({
    po_id: po.id,
    invoice_number: '',
    invoice_amount: po.total,
    invoice_date: new Date().toISOString().split('T')[0]
  });
  const [saving, setSaving] = useState(false);

  const amountMatch = Math.abs(form.invoice_amount - po.total) <= po.total * 0.01;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${API}/purchase-orders/${po.id}/supplier-invoice`, form);
      if (res.data.three_way_match) {
        toast.success('3-way match complete!');
      } else {
        toast.success('Supplier invoice recorded');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to record supplier invoice');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 bg-muted rounded-lg">
        <p className="font-semibold">{po.po_number}</p>
        <p className="text-sm text-muted-foreground">Supplier: {po.supplier_name}</p>
        <p className="text-sm font-mono mt-2">PO Total: {formatCurrency(po.total)}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Supplier Invoice Number *</Label>
          <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} required data-testid="supplier-inv-number" />
        </div>
        <div className="space-y-2">
          <Label>Invoice Date *</Label>
          <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} required />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Invoice Amount *</Label>
        <Input
          type="number"
          min="0"
          step="0.01"
          value={form.invoice_amount}
          onChange={(e) => setForm({ ...form, invoice_amount: parseFloat(e.target.value) || 0 })}
          required
        />
        {!amountMatch && (
          <p className="text-sm text-amber-600 flex items-center gap-1">
            <AlertTriangle className="h-4 w-4" />
            Amount differs from PO by more than 1%
          </p>
        )}
        {amountMatch && (
          <p className="text-sm text-emerald-600 flex items-center gap-1">
            <CheckCircle2 className="h-4 w-4" />
            Amount matches PO
          </p>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="record-invoice-btn">
          {saving ? 'Processing...' : 'Record Invoice'}
        </Button>
      </div>
    </form>
  );
};

// Main Purchase Orders Page
export const PurchaseOrdersPage = () => {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [poDialogOpen, setPoDialogOpen] = useState(false);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [poRes, projRes] = await Promise.all([
        axios.get(`${API}/purchase-orders`),
        axios.get(`${API}/projects`)
      ]);
      setPurchaseOrders(poRes.data);
      setProjects(projRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (poId) => {
    try {
      await axios.post(`${API}/purchase-orders/${poId}/approve`);
      toast.success('Purchase order approved');
      loadData();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const filteredPOs = purchaseOrders.filter(po =>
    po.po_number?.toLowerCase().includes(search.toLowerCase()) ||
    po.supplier_name?.toLowerCase().includes(search.toLowerCase())
  );

  // Summary stats
  const totalPOs = purchaseOrders.length;
  const pendingApproval = purchaseOrders.filter(p => p.status === 'pending_approval').length;
  const awaitingGoods = purchaseOrders.filter(p => p.status === 'approved' && !p.goods_received).length;
  const matched = purchaseOrders.filter(p => p.three_way_match).length;

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      
      {/* Header */}
<div className="flex items-center justify-between relative z-10">
  <div>
    <h1 className="text-3xl font-bold text-slate-900">Purchase Orders</h1>
    <p className="text-muted-foreground mt-1">
      Procurement with 3-way matching
    </p>
  </div>

  <Button
    className="z-50 relative"
    onClick={() => {
      setSelectedPO(null);
      setPoDialogOpen(true);
    }}
  >
    <Plus className="h-4 w-4 mr-2" />
    New Purchase Order
  </Button>
</div>

<Dialog open={poDialogOpen} onOpenChange={setPoDialogOpen}>
  <DialogContent className="max-w-3xl z-50">
    <DialogHeader>
      <DialogTitle>
        {selectedPO ? 'Edit Purchase Order' : 'New Purchase Order'}
      </DialogTitle>

      <DialogDescription>
        Create or update a purchase order.
      </DialogDescription>
    </DialogHeader>

    <PurchaseOrderForm
      po={selectedPO}
      projects={projects}
      onSave={loadData}
      onClose={() => setPoDialogOpen(false)}
    />
  </DialogContent>
</Dialog>
      

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="kpi-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total POs</p>
                <p className="text-2xl font-bold font-mono">{totalPOs}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Approval</p>
                <p className="text-2xl font-bold font-mono text-amber-600">{pendingApproval}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <Truck className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Awaiting Goods</p>
                <p className="text-2xl font-bold font-mono">{awaitingGoods}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">3-Way Matched</p>
                <p className="text-2xl font-bold font-mono text-emerald-600">{matched}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex justify-end">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search POs..." className="pl-9 w-64" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {/* PO Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>PO Number</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Delivery</TableHead>
              <TableHead>3-Way Match</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredPOs.map((po) => (
              <TableRow key={po.id}>
                <TableCell className="font-mono text-sm">{po.po_number}</TableCell>
                <TableCell className="font-medium">{po.supplier_name}</TableCell>
                <TableCell className="text-right font-mono font-semibold text-amber-600">
  {formatCurrency(po.total)}
</TableCell>
                <TableCell>{po.delivery_date?.split('T')[0] || '-'}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className={`h-3 w-3 rounded-full ${po.goods_received ? 'bg-emerald-500' : 'bg-slate-300'}`} title="Goods Received" />
                    <div className={`h-3 w-3 rounded-full ${po.supplier_invoice_number ? 'bg-emerald-500' : 'bg-slate-300'}`} title="Invoice Received" />
                    <div className={`h-3 w-3 rounded-full ${po.three_way_match ? 'bg-emerald-500' : 'bg-slate-300'}`} title="Matched" />
                  </div>
                </TableCell>
                <TableCell><Badge className={statusColors[po.status]}>{po.status.replace('_', ' ')}</Badge></TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setSelectedPO(po); setPoDialogOpen(true); }}>Edit</DropdownMenuItem>
                      {po.status === 'draft' && (
                        <DropdownMenuItem onClick={() => handleApprove(po.id)}>Submit for Approval</DropdownMenuItem>
                      )}
                      {po.status === 'pending_approval' && (
                        <DropdownMenuItem onClick={() => handleApprove(po.id)}>Approve</DropdownMenuItem>
                      )}
                      {po.status === 'approved' && !po.goods_received && (
                        <DropdownMenuItem onClick={() => { setSelectedPO(po); setReceiptDialogOpen(true); }}>
                          <Truck className="h-4 w-4 mr-2" /> Receive Goods
                        </DropdownMenuItem>
                      )}
                      {po.goods_received && !po.supplier_invoice_number && (
                        <DropdownMenuItem onClick={() => { setSelectedPO(po); setInvoiceDialogOpen(true); }}>
                          <FileText className="h-4 w-4 mr-2" /> Record Invoice
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={async () => {
                        try {
                        await axios.delete(`${API}/purchase-orders/${po.id}`);
                        toast.success('Purchase order deleted');
                        loadData();
                         } catch (error) {
                        toast.error('Failed to delete purchase order');
                         }
                         }}
                        className="text-red-600 hover:bg-red-50"
                        >
                         Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filteredPOs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No purchase orders found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Goods Receipt Dialog */}
      <Dialog open={receiptDialogOpen} onOpenChange={setReceiptDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Receive Goods</DialogTitle>
          </DialogHeader>
          {selectedPO && <GoodsReceiptForm po={selectedPO} onSave={loadData} onClose={() => setReceiptDialogOpen(false)} />}
        </DialogContent>
      </Dialog>

      {/* Supplier Invoice Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Record Supplier Invoice</DialogTitle>
          </DialogHeader>
          {selectedPO && <SupplierInvoiceForm po={selectedPO} onSave={loadData} onClose={() => setInvoiceDialogOpen(false)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
